import React, { useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import * as S from "./styles";
import * as SH from "../../utils/globalStyles/sheet";
import UserAvatar from "../userAvatar";
import { useUserContext } from "../../contexts/user/userContext";
import {
  IContact,
  updateContact,
} from "../../utils/services/modules/contacts";
import { getApiError } from "../../utils/services/database";
import { copyText, getFirstName } from "../../utils/user";

interface IContactSheet {
  contact: IContact | null;
  onClose: () => void;
  onSend: (code: number) => void;
}

// Detalhes de um contato: enviar moedas, copiar o ID ou remover
const ContactSheet: React.FC<IContactSheet> = ({
  contact,
  onClose,
  onSend,
}) => {
  const { state, dispatch } = useUserContext();
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [copied, setCopied] = useState(false);

  // mantém o último contato durante a animação de fechar
  const lastRef = useRef<IContact | null>(null);
  if (contact) lastRef.current = contact;
  const current = contact ?? lastRef.current;

  const reset = () => {
    setConfirming(false);
    setCopied(false);
  };

  const handleClose = () => {
    if (removing) return;
    onClose();
  };

  const handleCopy = async () => {
    if (!current) return;
    if (await copyText(String(current.code))) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRemove = async () => {
    if (!current || !state.user || removing) return;
    setRemoving(true);
    try {
      const contacts = await updateContact(
        current.code,
        "remove",
        state.user.token,
      );
      dispatch({ type: "UPDATE_CONTACTS", payload: contacts });
      toast.success(`${getFirstName(current.name)} foi removido dos contatos.`);
      onClose();
    } catch (error) {
      toast.error(getApiError(error, "Não foi possível remover o contato."));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <SH.Sheet
      anchor="bottom"
      open={!!contact}
      onOpen={() => undefined}
      onClose={handleClose}
      disableSwipeToOpen
      SlideProps={{ onExited: reset }}
    >
      <SH.Handle />
      {current && (
        <S.Content>
          <UserAvatar
            name={current.name}
            code={current.code}
            avatar={current.avatar}
            size={72}
          />
          <S.Name>{current.name}</S.Name>

          <S.CodeButton type="button" onClick={handleCopy}>
            ID {current.code}
            {copied ? <S.CopiedIcon /> : <S.CopyIcon />}
            <S.CopyLabel>{copied ? "Copiado" : "Copiar"}</S.CopyLabel>
          </S.CodeButton>

          {confirming ? (
            <S.ConfirmBox>
              <S.ConfirmText>
                Remover <strong>{current.name}</strong> dos seus contatos?
              </S.ConfirmText>
              <S.ConfirmRow>
                <S.SecondaryButton
                  type="button"
                  onClick={() => setConfirming(false)}
                >
                  Cancelar
                </S.SecondaryButton>
                <S.DangerButton type="button" onClick={handleRemove}>
                  {removing ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Remover"
                  )}
                </S.DangerButton>
              </S.ConfirmRow>
            </S.ConfirmBox>
          ) : (
            <S.Actions>
              <S.PrimaryButton
                type="button"
                onClick={() => onSend(current.code)}
              >
                <S.SendIcon />
                Enviar moedas
              </S.PrimaryButton>
              <S.TextDangerButton
                type="button"
                onClick={() => setConfirming(true)}
              >
                <S.RemoveIcon />
                Remover dos contatos
              </S.TextDangerButton>
            </S.Actions>
          )}
        </S.Content>
      )}
    </SH.Sheet>
  );
};

export default ContactSheet;
